import React, { useState, useEffect } from 'react';
import { 
  FaComments,
  FaEye,
  FaEnvelope,
  FaFilter,
  FaTimes as FaClose
} from 'react-icons/fa';

const AdminContactMessages = () => {
  const [contactMessages, setContactMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterMessageType, setFilterMessageType] = useState('');
  const [showContactMessageModal, setShowContactMessageModal] = useState(false);
  const [selectedContactMessage, setSelectedContactMessage] = useState(null);

  const fetchContactMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      let url = `/api/dashboard/admin/contact-messages?page=${currentPage}&limit=10`;
      if (filterMessageType) url += `&type=${filterMessageType}`;
      
      const messagesRes = await fetch(url, { headers });
      if (messagesRes.ok) {
        const messagesData = await messagesRes.json();
        setContactMessages(messagesData.messages);
        setTotalPages(messagesData.totalPages);
      }
    } catch (error) {
      console.error('Error fetching contact messages:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContactMessages();
  }, [currentPage, filterMessageType]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleViewContactMessage = (message) => {
    setSelectedContactMessage(message);
    setShowContactMessageModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-gray-400">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-gray-800 border border-gray-700 rounded-xl">
        {/* Filters */}
        <div className="p-6 border-b border-gray-700">
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Mesaj Türü</label>
              <select
                value={filterMessageType}
                onChange={(e) => {
                  setFilterMessageType(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Tüm Türler</option>
                <option value="general">Genel</option>
                <option value="support">Destek</option>
                <option value="feature">Özellik İsteği</option>
                <option value="bug">Hata Bildirimi</option>
                <option value="legal">Yasal</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={() => {
                  setFilterMessageType('');
                  setCurrentPage(1);
                }}
                className="px-4 py-2 bg-gray-600 text-gray-300 rounded-lg hover:bg-gray-500 transition-colors"
              >
                <FaFilter className="inline mr-2" />
                Filtreleri Temizle
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <h3 className="text-xl font-semibold text-white mb-6">İletişim Mesajları</h3>
          
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Gönderen</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Konu</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Tür</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Mesaj</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Tarih</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {contactMessages.map((message) => (
                  <tr key={message._id} className="hover:bg-gray-700/50">
                    <td className="px-6 py-4 whitespace-nowrap text-left">
                      <div className="text-sm font-medium text-white">{message.name}</div>
                      <div className="text-sm text-gray-400">{message.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-left">
                      <div className="text-sm text-gray-300">{message.subject}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-left">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${
                        message.type === 'general' ? 'text-blue-400 bg-blue-900/20 border-blue-500/30' :
                        message.type === 'support' ? 'text-green-400 bg-green-900/20 border-green-500/30' :
                        message.type === 'feature' ? 'text-purple-400 bg-purple-900/20 border-purple-500/30' :
                        message.type === 'bug' ? 'text-red-400 bg-red-900/20 border-red-500/30' :
                        message.type === 'legal' ? 'text-yellow-400 bg-yellow-900/20 border-yellow-500/30' :
                        'text-gray-400 bg-gray-900/20 border-gray-500/30'
                      }`}>
                        {message.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-left">
                      <div className="text-sm text-gray-300 max-w-xs truncate">
                        {message.message}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400 text-left">
                      {formatDate(message.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 text-left">
                      <button
                        onClick={() => handleViewContactMessage(message)}
                        className="text-blue-400 hover:text-blue-300 flex items-center"
                        title="Detayları Görüntüle"
                      >
                        <FaEye className="h-4 w-4 mr-1" />
                        Detay
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-lg text-gray-300 bg-gray-700 hover:bg-gray-600 disabled:opacity-50"
            >
              Önceki
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-lg text-gray-300 bg-gray-700 hover:bg-gray-600 disabled:opacity-50"
            >
              Sonraki
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-400">
                Sayfa <span className="font-medium text-white">{currentPage}</span> /{' '}
                <span className="font-medium text-white">{totalPages}</span>
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-lg shadow-sm -space-x-px">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-lg border border-gray-600 bg-gray-700 text-sm font-medium text-gray-300 hover:bg-gray-600 disabled:opacity-50"
                >
                  Önceki
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-lg border border-gray-600 bg-gray-700 text-sm font-medium text-gray-300 hover:bg-gray-600 disabled:opacity-50"
                >
                  Sonraki
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Contact Message Detail Modal */}
      {showContactMessageModal && selectedContactMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-start justify-between">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-600/20 rounded-lg flex items-center justify-center mr-4">
                    <FaEnvelope className="h-6 w-6 text-blue-400" />
                  </div>
                  <div className="text-left">
                    <h2 className="text-2xl font-bold text-white">İletişim Mesajı</h2>
                    <p className="text-gray-400">#{selectedContactMessage._id.slice(-8)}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowContactMessageModal(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <FaClose className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="space-y-6">
                {/* Sender Info */}
                <div className="bg-gray-700 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-white mb-4">Gönderen Bilgileri</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="text-gray-400 text-sm">Ad Soyad:</span>
                      <p className="text-white font-medium">{selectedContactMessage.name}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">E-posta:</span>
                      <p className="text-white font-medium">{selectedContactMessage.email}</p>
                    </div>
                  </div>
                </div>

                {/* Message Details */}
                <div className="bg-gray-700 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-white mb-4">Mesaj Detayları</h3>
                  <div className="space-y-4">
                    <div>
                      <span className="text-gray-400 text-sm">Konu:</span>
                      <p className="text-white font-medium">{selectedContactMessage.subject}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Tür:</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ml-2 ${
                        selectedContactMessage.type === 'general' ? 'text-blue-400 bg-blue-900/20 border-blue-500/30' :
                        selectedContactMessage.type === 'support' ? 'text-green-400 bg-green-900/20 border-green-500/30' :
                        selectedContactMessage.type === 'feature' ? 'text-purple-400 bg-purple-900/20 border-purple-500/30' :
                        selectedContactMessage.type === 'bug' ? 'text-red-400 bg-red-900/20 border-red-500/30' :
                        selectedContactMessage.type === 'legal' ? 'text-yellow-400 bg-yellow-900/20 border-yellow-500/30' :
                        'text-gray-400 bg-gray-900/20 border-gray-500/30'
                      }`}>
                        {selectedContactMessage.type}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Mesaj:</span>
                      <div className="mt-2 p-3 bg-gray-600 rounded-lg">
                        <p className="text-white whitespace-pre-wrap">{selectedContactMessage.message}</p>
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Gönderilme Tarihi:</span>
                      <p className="text-white">{formatDate(selectedContactMessage.createdAt)}</p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => window.open(`mailto:${selectedContactMessage.email}?subject=Re: ${selectedContactMessage.subject}`, '_blank')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                  >
                    <FaEnvelope className="h-4 w-4 mr-2" />
                    E-posta Gönder
                  </button>
                  <button
                    onClick={() => setShowContactMessageModal(false)}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Kapat
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminContactMessages;
